import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import dayjs, { type Dayjs } from 'dayjs'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { MedicalBackgroundQuestionnaireItem } from 'components/Assessments/FuaAssessment/sections/MedicalBackground/questionnaire'
import { EndocrineFormField } from './formField'

/**
 * Endocrine subsection adapter
 * Handles endocrine system assessment and menstrual cycle tracking (5 fields)
 */
export class EndocrineAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Endocrine assessment fields
    const endocrineWnl = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_WNL
    )?.at(0)

    const menstrualCycle = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_CYCLE
    )?.at(0)

    const menstrualDate = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_DATE
    )?.at(0)

    const endocrineNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [EndocrineFormField.ENDOCRINE_ENDOCRINE_WNL]: endocrineWnl,
      [EndocrineFormField.ENDOCRINE_MENSTRUAL_CYCLE]: menstrualCycle,
      [EndocrineFormField.ENDOCRINE_MENSTRUAL_DATE]: menstrualDate
        ? dayjs(menstrualDate)
        : undefined,
      [EndocrineFormField.ENDOCRINE_ENDOCRINE_NOTES]: endocrineNotes,
      [EndocrineFormField.ENDOCRINE_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_WNL,
        value: this.getFormDataField(EndocrineFormField.ENDOCRINE_ENDOCRINE_WNL)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_SYSTEM_SUMMARY,
        value: this.getFormDataField(EndocrineFormField.ENDOCRINE_SYSTEM_SUMMARY)
      }
    ]

    // Conditionally add menstrual cycle fields if they exist (they are conditionally rendered)
    if (this.formDataFieldExists(EndocrineFormField.ENDOCRINE_MENSTRUAL_CYCLE)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_CYCLE,
        value: this.getFormDataField(EndocrineFormField.ENDOCRINE_MENSTRUAL_CYCLE)
      })
    }

    if (this.formDataFieldExists(EndocrineFormField.ENDOCRINE_MENSTRUAL_DATE)) {
      const dateValue = this.getFormDataField(EndocrineFormField.ENDOCRINE_MENSTRUAL_DATE)
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_DATE,
        value: dateValue ? (dateValue as Dayjs).utc().format('YYYY-MM-DD') : undefined
      })
    }

    if (this.formDataFieldExists(EndocrineFormField.ENDOCRINE_ENDOCRINE_NOTES)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_NOTES,
        value: this.getFormDataField(EndocrineFormField.ENDOCRINE_ENDOCRINE_NOTES)
      })
    }

    return mutations
  }
}
